import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import PageHeading from '../PageHeading/PageHeading';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import mainApi from '../../../assets/api/MainApi';
import { UserContext } from '../../../assets/contexts/userContext';
import {
    ADVERTISING_CAMPAIGNS_CREATE_LINK,
    ADVERTISING_CAMPAIGNS_MAIN_LINK,
    PLATFORM_MAIN_LINK,
} from '../../../assets/utils/constants';
import {
    ADVERTISING_CAMPAIGNS_LIMIT,
    formatAdvertisingDateOnly,
    getCampaignStatusTitle,
    getShopId,
    parseAdvertisingApiError,
} from './advertisingCampaignsHelpers';

function AdvertisingCampaignList() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const currentShopId = getShopId(user?.default_shop);
    const [campaigns, setCampaigns] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isMoreLoading, setIsMoreLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user) return undefined;

        let isCancelled = false;
        setIsLoading(true);
        setError('');

        mainApi.getAdvertisingCampaigns({
            store_id: currentShopId,
            status: '',
            active_only: false,
            limit: ADVERTISING_CAMPAIGNS_LIMIT,
            offset: 0,
        })
            .then((res) => {
                if (isCancelled) return;
                setCampaigns(res?.items || []);
                setTotal(Number(res?.total || 0));
                setOffset((res?.items || []).length);
            })
            .catch((err) => {
                console.log(err);
                if (!isCancelled) {
                    setCampaigns([]);
                    setTotal(0);
                    setOffset(0);
                    setError(parseAdvertisingApiError(err));
                }
            })
            .finally(() => {
                if (!isCancelled) setIsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [user, currentShopId]);

    function handleLoadMore() {
        setIsMoreLoading(true);
        setError('');

        mainApi.getAdvertisingCampaigns({
            store_id: currentShopId,
            status: '',
            active_only: false,
            limit: ADVERTISING_CAMPAIGNS_LIMIT,
            offset,
        })
            .then((res) => {
                const nextItems = res?.items || [];
                setCampaigns((prevValue) => [...prevValue, ...nextItems]);
                setTotal(Number(res?.total || 0));
                setOffset((prevValue) => prevValue + nextItems.length);
            })
            .catch((err) => {
                console.log(err);
                setError(parseAdvertisingApiError(err));
            })
            .finally(() => {
                setIsMoreLoading(false);
            });
    }

    const hasMore = campaigns.length < total;

    function renderCampaignPeriod(campaign) {
        if (!campaign.started_at && !campaign.ends_at) return null;

        return (
            <p className='advertising-campaigns-list__item-period'>
                {campaign.started_at ? formatAdvertisingDateOnly(campaign.started_at) : ''}
                {campaign.started_at && campaign.ends_at ? ' - ' : ''}
                {campaign.ends_at ? formatAdvertisingDateOnly(campaign.ends_at) : ''}
            </p>
        );
    }

    return (
        <div className='advertising-campaigns-list'>
            <PageHeading
                className='advertising-campaigns-list__heading'
                title='Ваши рекламные кампании'
                actions={[
                    {
                        id: 'advertising-create',
                        name: 'Новая заявка',
                        isMainAction: true,
                        onClick: () => navigate(`${location.pathname}/${ADVERTISING_CAMPAIGNS_CREATE_LINK}`),
                    },
                ]}
            />

            {error ? <p className='advertising-campaigns__error'>{error}</p> : null}

            {isLoading ? (
                <div className='advertising-campaigns__preloader'>
                    <MiniPreloader />
                </div>
            ) : campaigns.length > 0 ? (
                <>
                    <div className='advertising-campaigns-list__items'>
                        {campaigns.map((campaign) => (
                            <Link
                                className={`advertising-campaigns-list__item advertising-campaigns-list__item_status_${campaign.status || 'default'}`}
                                to={`${PLATFORM_MAIN_LINK}/${ADVERTISING_CAMPAIGNS_MAIN_LINK}/${campaign.id}`}
                                key={`advertising-campaign-${campaign.id}`}
                            >
                                <div className='advertising-campaigns-list__item-main'>
                                    <div>
                                        <p className='advertising-campaigns-list__item-title'>
                                            {campaign.store_name || 'Магазин'} - {campaign.tariff_title || campaign.tariff_code || 'Тариф'}
                                        </p>
                                        {renderCampaignPeriod(campaign)}
                                    </div>
                                    <span className={`advertising-campaigns__status advertising-campaigns__status_${campaign.status || 'default'}`}>
                                        {getCampaignStatusTitle(campaign)}
                                    </span>
                                </div>
                                <span className='advertising-campaigns-list__item-arrow' aria-hidden='true' />
                            </Link>
                        ))}
                    </div>
                    {hasMore ? (
                        <button
                            className='advertising-campaigns__secondary-btn advertising-campaigns-list__more'
                            type='button'
                            onClick={handleLoadMore}
                            disabled={isMoreLoading}
                        >
                            {isMoreLoading ? <MiniPreloader /> : 'Показать еще'}
                        </button>
                    ) : null}
                </>
            ) : (
                <div className='advertising-campaigns__empty'>
                    <p className='advertising-campaigns__empty-title'>Заявок на ведение рекламы пока нет</p>
                    <button
                        className='advertising-campaigns__main-btn'
                        type='button'
                        onClick={() => navigate(`${location.pathname}/${ADVERTISING_CAMPAIGNS_CREATE_LINK}`)}
                    >
                        Новая заявка
                    </button>
                </div>
            )}
        </div>
    );
}

export default AdvertisingCampaignList;
